import { Button, Checkbox, Drawer, Dropdown, Input, Menu, Modal, Select, Switch, TimePicker } from "antd";
import "./ScreenSetUpModal.scss";
import React, { ReactNode, useEffect, useState } from "react";
import DSAApi from "../../api/DSAApi";
import { handleServerError } from "../../util/ErrorHandler";
import { DsaScreenAddDto, DsaScreenDto } from "../../generated/dashboard-client";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { CloseOutlined, LeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { getTimeAgoFromString } from "../../util/utils";
import moment from "moment";
import { locationsState, mobileState } from "../../state/atoms";
import { useRecoilState, useRecoilValue } from "recoil";

const { Option } = Select;

const Days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export interface ManageScreenModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSaved: (device: DsaScreenDto, timezone: string, rotation: number, scheduledReboot: boolean, restartDay: number, restartTime: string) => void;
  currentScreen: DsaScreenDto;
  withTooltip: (tooltip: string, object: ReactNode) => ReactNode;
  openMobileMenu: () => void;
}

export function ManageScreenModal({ isVisible, onClose, onSaved, currentScreen, withTooltip, openMobileMenu }: ManageScreenModalProps) {
  const [locations] = useRecoilState(locationsState);

  const [name, setName] = useState("");
  const [placement, setPlacement] = useState("");
  const [loc, setLoc] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [orientation, setOrientation] = useState("");
  const [hasReboot, setReboot] = useState(false);
  const [rebootDays, setRebootDays] = useState(Days.map(day => ({ day, selected: false })));
  const [rebootTime, setRebootTime] = useState('');
  const [loading, setLoading] = useState(false);

  const mobile = useRecoilValue(mobileState);

  useEffect(() => {
    if (currentScreen?.serial) {
      setName(currentScreen.name!);
      setPlacement(currentScreen.placement!);
      setLoc(currentScreen.locationId!.toString());
      setTimeZone(currentScreen.timezone!);
      setOrientation(currentScreen.rotation!.toString());
      setReboot(currentScreen.scheduledReboot!);
      if (currentScreen.scheduledReboot) {
        const thisDate = moment(currentScreen.restartTime!, "HH:mm:ss").format("HH:mm");
        setRebootTime(thisDate);
        const theseDays = currentScreen.restartDay !== 0 ?
          Days.map((day, idx) => {
            return { day: day, selected: currentScreen.restartDay! - 1 === idx }
          }) :
          Days.map(day => {
            return { day: day, selected: true }
          });
        setRebootDays(theseDays);
      }
    }
  }, [currentScreen])

  async function update() {
    try {
      setLoading(true);
      let restartDay = 0;
      if (rebootDays.filter(it => it.selected).length === 1) {
        restartDay = rebootDays.findIndex(it => it.selected) + 1;
      }

      const resp = await DSAApi.updateScreen(currentScreen.serial!, {
        name: name,
        placement: placement,
        locationId: parseInt(loc),
        timezone: timeZone,
        rotation: parseInt(orientation),
        scheduledReboot: hasReboot,
        restartDay: restartDay,
        restartTime: `${rebootTime}:00`
      } as DsaScreenAddDto
      );
      setLoading(false);
      onSaved(resp.data, timeZone, parseInt(orientation), hasReboot, restartDay, rebootTime);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }

  function toggleRebootDay(ev: CheckboxChangeEvent, day: string) {
    ev.stopPropagation();
    setRebootDays([...rebootDays.map(it => {
      it.selected = it.day === day;
      return it;
    })]);
  }

  function rebootAll() {
    rebootDays.forEach(it => it.selected = true);
    setRebootDays([...rebootDays]);
  }

  function getDateString(date: string) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString("en-us");
  }

  function onBackClick() {
    openMobileMenu();
    onClose();
  }

  function onRebootTimeOpenChange(open: boolean) {
    if (!open) {
      const timeValue = document.getElementById('rebootTimePicker')?.getAttribute('value');
      if (timeValue) {
        setRebootTime(moment(timeValue, 'hh:mm A').format('HH:mm'));
      }
    }
  }

  function setRebootToNow() {
    setRebootTime(moment().format('HH:mm'));
    setTimeout(() => {
      document.querySelectorAll('.ant-picker-ok .ant-btn').forEach(btn => {
        (btn as HTMLElement).click();
      })
    }, 50);
  }

  const RebootDaysMenu = <Menu>
    <div className={"reboots"}>
      <Button onClick={rebootAll}>Reboot Daily</Button>
      <ul>
        {rebootDays.map(it => <li key={it.day}>
          <Checkbox checked={it.selected} onChange={(ev) => toggleRebootDay(ev, it.day)}
            onClick={ev => ev.stopPropagation()} />
          {it.day}
        </li>)}
      </ul>
    </div>
  </Menu>;

  const content = (<>
    <header>
      <span className="title-help"><h2>Manage your screen</h2></span>
      {mobile && <LeftOutlined onClick={() => onBackClick()} className="close-btn back-btn" />}
      <CloseOutlined onClick={() => onClose()} className="close-btn" />
    </header>

    <div className="details-section">
      <div className="details">
        <div>
          <p><b>Status:</b> {currentScreen?.online ? 'Online' : 'Offline'}</p>
          <p><b>Last Online:</b> {getTimeAgoFromString(currentScreen?.lastOnline!)}</p>
          <p><b>Created On:</b> {getDateString(currentScreen?.createdOn!)}</p>
        </div>
        <div>
          <p><b>IP Address:</b> {currentScreen?.ipAddress}</p>
          <p><b>Network:</b> {currentScreen?.network}</p>
          <p><b>Serial Number:</b> {currentScreen?.serial}</p>
        </div>
      </div>
    </div>

    <div>
      <div>
        <label>Screen Name</label>
        <Input size={"large"} placeholder={"Name your screen"}
          value={name} onChange={ev => setName(ev.target.value)} />
      </div>
    </div>

    <div className={"split"}>
      <div>
        <label>Placement</label>
        <Input size={"large"} placeholder={"Placement"}
          value={placement} onChange={ev => setPlacement(ev.target.value)} />
      </div>
      <div>
        <label>Business Location</label>
        <Select size={"large"} placeholder="Location Assignment" value={loc} onChange={val => setLoc(val)} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          {locations.map(it => <Option key={it.internalId}>
            {it.name}
          </Option>)}
        </Select>
      </div>
    </div>
    <div className={"split"}>
      <div>
        <label>Time Zone</label>
        <Select size={"large"} placeholder="Time Zone" value={timeZone} onChange={val => setTimeZone(val)} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <Option key="America/Los_Angeles">Pacific Time - Los Angeles</Option>
          <Option key="America/Phoenix">Mountain Time - Phoenix</Option>
          <Option key="America/Denver">Mountain Time - Denver</Option>
          <Option key="America/Chicago">Central Time - Chicago</Option>
          <Option key="America/New_York">Eastern Time - New York</Option>
          <Option key="America/Anchorage">Alaska Time - Anchorage</Option>
          <Option key="Pacific/Honolulu">Hawaii Time - Honolulu</Option>
        </Select>
      </div>
      <div>
        <label>Orientation</label>
        <Select size={"large"} placeholder="Orientation" value={orientation} onChange={val => setOrientation(val)} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <Option key="0">0° Landscape</Option>
          <Option key="90">90° Portrait</Option>
          <Option key="270">180° Portrait Flipped</Option>
        </Select>
      </div>
    </div>

    <div className={"reboot"}>
      <div className={"hd"}>
        <label>Schedule Reboot</label>
        <Switch checked={hasReboot} onChange={() => setReboot(!hasReboot)} />
      </div>

      {!hasReboot && <div className={"cover"}></div>}

      <div className={`split`}>
        <div>
          <label>Reboot Day</label>
          <Dropdown overlay={RebootDaysMenu}>
            <Button size={"large"}>
              {rebootDays.filter(it => it.selected).length === 0 ? <span className="not-selected">Select Day</span> :
                rebootDays.filter(it => it.selected).length === 1 ? rebootDays.find(it => it.selected)!.day :
                  'Daily'}
            </Button>
          </Dropdown>
        </div>
        <div>
          <label>Reboot Time</label>
          <TimePicker
            id="rebootTimePicker"
            placeholder={"Select Time"}
            suffixIcon={null}
            value={rebootTime ? moment(rebootTime, "HH:mm") : null}
            format="hh:mm A"
            use12Hours={true}
            minuteStep={15}
            onChange={(time, str) => setRebootTime(str ? moment(str, 'hh:mm A').format('HH:mm') : '')}
            onOpenChange={(open) => onRebootTimeOpenChange(open)}
            showNow={false}
            renderExtraFooter={() => (<div>
              <span onClick={() => setRebootToNow()}>Now</span>
            </div>)}
          />
        </div>
      </div>
    </div>

    <footer>
      {<Button className={"primary"} size={"large"} loading={loading} onClick={() => update()} disabled={!orientation || !timeZone || (hasReboot && (rebootDays.filter(it => it.selected).length === 0 || !rebootTime))}>
        Done
      </Button>}
      {!mobile && withTooltip("Help", <a className="help-btn" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029534-manage-screens" target="_blank" rel="noreferrer">
        <QuestionCircleOutlined />
      </a>)}
      {mobile && <a className="help-link" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029534-manage-screens" target="_blank" rel="noreferrer">
        <QuestionCircleOutlined/>
        Help
      </a>}
    </footer>
  </>)

  return (
    !mobile ?
    <Modal
      className={"clean screen-set-up manage-screen"}
      visible={isVisible}
      onCancel={() => onClose()}
      width={600}
    >
      {content}
    </Modal>
    :
    <Drawer
      placement={"bottom"}
      visible={isVisible}
      className={"mobile-bottom-menu screen-set-up manage-screen"}
      height={"80vh"}
      closeIcon={false}
      maskClosable={true}
      onClose={() => onClose()}
    >
        {content}
    </Drawer>
  );
}
